import { ApiError, errorFromResponse, isNotImplemented, networkError } from './errors';

export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ?? 'https://business.clever-booking.com';

/** Prefix of every marketplace endpoint. */
export const MARKETPLACE = '/api/v1/marketplace';

/**
 * Shared fetch wrapper (always sends the session cookie). Throws `ApiError`
 * for non-2xx responses and for network failures; resolves `undefined` for 204.
 */
export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  let r: Response;
  try {
    r = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      credentials: 'include',
      headers: {
        'Accept-Language': navigator.language || 'en',
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...(init.headers ?? {}),
      },
    });
  } catch (e) {
    // A cancelled request is not a network failure — let the caller see it.
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw networkError(e, path);
  }
  if (!r.ok) throw await errorFromResponse(r, path);
  if (r.status === 204) return undefined as T;
  const text = await r.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/**
 * Like `api()`, but resolves `null` when the endpoint isn't on the backend yet
 * (404/405/501 without an error code) or answers 204. Use for redesign reads
 * that must degrade gracefully.
 */
export async function apiOrNull<T>(
  path: string,
  init: RequestInit = {},
  opts: { nullOnUnauthorized?: boolean } = {},
): Promise<T | null> {
  try {
    const out = await api<T>(path, init);
    return out ?? null;
  } catch (e) {
    if (isNotImplemented(e)) return null;
    if (opts.nullOnUnauthorized && e instanceof ApiError && e.status === 401) return null;
    throw e;
  }
}

/** Query keys for the marketplace. Prefixes double as invalidation targets. */
export const marketplaceKeys = {
  me: ['marketplace-me'] as const,
  myBookingsAll: ['marketplace-my-bookings'] as const,
  myBookings: (past: boolean) => ['marketplace-my-bookings', past] as const,
  search: (qs: string) => ['marketplace-search', qs] as const,
  salon: (subdomain: string | undefined) => ['marketplace-salon', subdomain] as const,
  availabilityAll: ['marketplace-availability'] as const,
  availabilityRangeAll: ['marketplace-availability-range'] as const,
  availabilityRange: (subdomain: string, qs: string) =>
    ['marketplace-availability-range', subdomain, qs] as const,
  rebookSuggestion: ['marketplace-rebook-suggestion'] as const,
  preferences: ['marketplace-preferences'] as const,
  salonReviewsAll: (subdomain: string | undefined) =>
    ['marketplace-salon-reviews', subdomain] as const,
  salonReviews: (subdomain: string | undefined, page: number, size: number) =>
    ['marketplace-salon-reviews', subdomain, page, size] as const,
};
